<template>
  <v-card style="height: 100%; overflow: hidden">
    <ChartControl
      v-on:update-ranges="newData"
      :enabled="settingsEnabled"
      :loading="loading"
      :module="module"
    />
    <v-row no-gutters style="height: 90%">
      <e-chart
        v-if="opts.series.length > 0"
        :options="opts"
        style="width: 100%; max-width: 100%; min-height: 100%; max-height: 100%"
        :autoresize="true"
        :theme="this.darkmode ? 'sensoronlinedark' : 'macarons'"
      />
    </v-row>
  </v-card>
</template>

<script>
import ChartControl from "@/components/chart/ChartControl";
import { mapActions, mapState } from "vuex";
import "echarts/lib/component/title";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";
import "echarts/lib/component/dataZoom";
import "echarts/lib/chart/line";
import "echarts/lib/coord/cartesian/Grid";
import Meta from "@/_helpers/ModuleMeta";
import i18n from "@/plugins/i18n";

export default {
  name: "Multichart",

  mixins: [Meta],

  props: {
    settingsEnabled: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
    },
    module: {
      default: {},
    },
    tag: {
      default: null,
    },
    timerTick: {
      default: null,
    },
    fakeData: {
      default: false,
    },
  },

  data() {
    return {
      startDate: new Date(new Date().setHours(new Date().getHours() - 24)),
      endDate: new Date(),
      digital: false,
      axisSync: false,
      dataZoom: false,
      boundAlarm: null,
      alarmHigh: null,
      alarmLow: null,
      colors: ["#5470C6", "#91CC75", "#EE6666"],
      opts: {
        legend: {
          show: true,
          data: [],
        },
        grid: {
          top: 55,
          bottom: 50,
          left: 80,
          right: 80,
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
          },
        },
        xAxis: {
          type: "time",
          splitLine: {
            show: false,
          },
        },
        yAxis: [],
        dataZoom: [
          {
            show: true,
            type: "inside",
            throttle: 50,
          },
        ],
        series: [],
      },
    };
  },

  computed: {
    ...mapState("configuration", ["darkmode"]),
    ...mapState("alarms", ["alarms"]),
  },

  methods: {
    ...mapActions("tagData", ["getTagDataForModule"]),
    ...mapActions("alarms", ["getAlarms"]),

    async updateData() {
      let to = new Date();
      let data = await this.getTagDataForModule({
        moduleId: this.module.dashboardModuleId,
        metrics: {
          from: this.endDate.toISOString(),
          to: to.toISOString(),
        },
      });

      if (data != undefined && data.length > 0) {
        data.forEach((d) => this.setData(d.fieldName, d.value, d.date));
        this.endDate = to;
        this.updateAlarm();
      }
    },

    async newData(metrics) {
      if (metrics == undefined) return;

      let data = await this.getTagDataForModule({
        moduleId: this.module.dashboardModuleId,
        metrics: {
          from: metrics.from,
          to: metrics.to,
        },
      });

      if (data != undefined && data.length > 0) {
        this.opts.series = [];
        this.opts.legend.data = [];
        data.forEach((d) => this.setData(d.fieldName, d.value, d.date));

        this.startDate = new Date(metrics.from);
        this.endDate = new Date(metrics.to);

        this.updateColors();
        this.setAlarmSeries();
      }
    },

    setData(name, value, date) {
      // Check if fieldname exist in series
      for (var i in this.opts.series) {
        if (this.opts.series[i].name == name) {
          if (!this.opts.legend.data.includes(name))
            this.opts.legend.data.push(name);

          this.opts.series[i].data.push({
            name: new Date(date).toString(),
            value: [date, value],
          });
          return;
        }
      }

      let series = {
        name: name,
        type: "line",
        data: [],
        areaStyle: { opacity: 0.2 },
        itemStyle: {},
        lineStyle: {},
        step: false,
      };

      if (this.digital) {
        series.step = "middle";
        series.symbolSize = 6;
        series.lineStyle.width = 4;
      }

      if (this.axisSync) {
        this.addSideScale(name);
        series.yAxisIndex = this.opts.series.length;
      } else if (!this.axisSync && this.opts.yAxis.length == 0)
        this.opts.yAxis.push({
          type: "value",
        });

      this.opts.series.push(series);
    },

    addSideScale(name) {
      // Check if fieldname already has an y-axis
      for (var i in this.opts.yAxis)
        if (this.opts.yAxis[i].name == name) return;

      let curLen = this.opts.yAxis.length;

      this.opts.yAxis.push({
        type: "value",
        name: name,
        position: curLen > 0 ? "right" : "left",
        offset: curLen > 1 ? (curLen - 1) * 70 : 4,
        nameLocation: (curLen - 1) % 2 == 1 ? "start" : "end",
        axisLine: {
          show: true,
          lineStyle: {
            width: 2,
          },
        },
        axisLabel: {
          show: true,
        },
      });

      // Make space for the new scale
      this.opts.grid.right += 10 + 16 * curLen;
    },

    addDataZoom() {
      this.opts.dataZoom.push({
        type: "slider",
        show: true,
        xAxisIndex: [0],
        height: 50,
        bottom: 15,
      });

      this.opts.grid.bottom += 40;
    },

    setAlarmSeries() {
      if (this.boundAlarm == null) return;

      this.alarmHigh = {
        name: i18n.t("alarm.fields.limitHigh") + " - " + this.boundAlarm.name,
        type: "line",
        data: [],
        symbolSize: 0,
        itemStyle: { color: "#C00000" },
        lineStyle: { width: 4 },
        z: 4,
      };

      this.alarmLow = {
        name: i18n.t("alarm.fields.limitLow") + " - " + this.boundAlarm.name,
        type: "line",
        data: [],
        symbolSize: 0,
        itemStyle: { color: "#000078" },
        lineStyle: { width: 4 },
        z: 4,
      };

      this.opts.series.push(this.alarmHigh);
      this.opts.series.push(this.alarmLow);

      let timeDiff = this.endDate.getTime() - this.startDate.getTime();

      let dates = [];
      //spread 31 data points over current timespan to create the alarm line
      for (let i = 0; i <= 30; i++) {
        dates.push(new Date(this.startDate.getTime() + (timeDiff / 30) * i));
      }

      dates.forEach((d) => {
        this.alarmHigh.data.push({
          value: [d, this.boundAlarm.limitHigh],
        });
      });

      dates.forEach((d) => {
        this.alarmLow.data.push({
          value: [d, this.boundAlarm.limitLow],
        });
      });
    },

    updateAlarm() {
      if (!this.alarmHigh || !this.alarmLow) return;

      this.alarmHigh.data.push({
        value: [new Date(), this.boundAlarm.limitHigh],
      });

      this.alarmLow.data.push({
        value: [new Date(), this.boundAlarm.limitLow],
      });
    },

    async setMetaParams() {
      let digital = await this.getMeta(this.module.meta, Meta.Enum.DIGITAL);

      if (digital) this.digital = digital.value === "true";

      let sync = await this.getMeta(this.module.meta, Meta.Enum.SYNC_Y);

      if (sync && !this.digital) this.axisSync = sync.value === "true";

      let zoomSlider = await this.getMeta(
        this.module.meta,
        Meta.Enum.ZOOM_SLIDER
      );

      if (zoomSlider) this.zoomSlider = zoomSlider.value === "true";

      let boundAlarm = await this.getMeta(this.module.meta, Meta.Enum.ALARM_ID);

      if (boundAlarm) {
        this.boundAlarm = this.alarms.find(
          (a) => a.alarmId == boundAlarm.valueMatch
        );
      }
    },

    updateColors() {
      if (!this.module.meta) return;
      let styleMetas = this.module.meta.filter((m) =>
        m.key.startsWith("so_style_meta")
      );
      for (var j in this.opts.series) {
        for (let i in styleMetas) {
          if (styleMetas[i].value == this.opts.series[j].name) {
            this.opts.series[j].itemStyle.color = styleMetas[i].content;

            if (this.axisSync)
              this.opts.yAxis[j].axisLine.lineStyle.color =
                styleMetas[i].content;
          }
        }
      }
    },

    setFakeData(name) {
      var base = +new Date(1988, 9, 3);
      var oneDay = 24 * 3600 * 1000;
      var lastVal = Math.random(0, 20);
      for (var i = 1; i < 10; i++) {
        var now = new Date((base += oneDay));
        let d = [now.getFullYear(), now.getMonth() + 1, now.getDate()].join(
          "/"
        );
        let val = Math.round((Math.random() - 0.1) * 60 + lastVal);
        this.setData(name, val, d);
        lastVal = val;
      }
    },
  },

  async created() {
    if (!this.fakeData) {
      let storedTimeOffset = this.getModuleSettingsTimeOffset(
        this.module.dashboardModuleId
      );

      if (storedTimeOffset != undefined) {
        this.startDate = new Date(new Date() - storedTimeOffset);
        this.endDate = new Date();
      }

      let data = await this.getTagDataForModule({
        moduleId: this.module.dashboardModuleId,
        metrics: {
          from: this.startDate.toISOString(),
          to: this.endDate.toISOString(),
        },
      });

      await this.setMetaParams();

      if (data != undefined) {
        data.forEach((d) => this.setData(d.fieldName, d.value, d.date));
      }

      if (this.boundAlarm != null) {
        this.getAlarms();
        this.setAlarmSeries();
      }
    } else {
      // Add fake data
      let ss = [];
      if (
        this.module.meta != undefined &&
        this.module.meta.filter((m) => m.key == "so_style_meta_color").length >
          0
      ) {
        for (var j in this.module.meta.filter(
          (m) => m.key == "so_style_meta_color"
        )) {
          ss.push(this.module.meta[j].value);
        }
      } else {
        ss.push("Device - Sensor 1");
      }

      for (var j in ss) {
        this.setFakeData(ss[j]);
      }
    }

    if (this.zoomSlider) this.addDataZoom();

    this.updateColors();
  },

  components: {
    ChartControl,
  },

  watch: {
    timerTick(v) {
      if (v % 6 == 0) {
        this.updateData();
      }
    },

    module() {
      if (this.fakeData && this.module.meta.length > 0) {
        this.opts.series = [];
        this.opts.legend.data = [];
        for (var i in this.module.meta.filter(
          (m) => m.key == "so_style_meta_color"
        )) {
          let name = `Device - Sensor ${i}`;
          if (this.module.meta[i].value) name = this.module.meta[i].value;
          this.setFakeData(name);
        }

        this.updateColors();
      }
    },
  },
};
</script>